import { Button, Card, CardActions, CardContent, Dialog, DialogContent, DialogTitle, Grid, LinearProgress, List, ListItem, ListItemText, TextField, Typography } from "@material-ui/core";
import { useEffect, useState } from "react";
import { IEquipmentInterfaceSummary, IEquipmentSummary } from "../../_types/member";
import { ServerTestMode } from '../../_service/auth';
import { IQRScanResult, IQRScanMemberRequest } from "../../_types/location";
import { RemoteStart as startEquipment } from '../../_service/location';

interface EquipmentLocation {
  location_id: number
  location_name: string
}

interface Props {
  equipment: IEquipmentSummary[]
  membershipId: string
  userId: number
  show: boolean
  onClose: () => void;
}

export const MemberRemoteStartDialog: React.FC<Props> = (props: Props) => {
  // We need to determin what equipment we need to start.
  // Is there more than one location provided? (networked location)

  const [equipmentLocation, setEquipmentLocation] = useState<EquipmentLocation | null>(null);
  const [equipmentEquipment, setEquipmentEquipment] = useState<IEquipmentSummary | null>(null);
  const [equipmentInterface, setEquipmentInterface] = useState<IEquipmentInterfaceSummary | null>(null);

  useEffect(() => {
    const locations = getLocations();
    if (locations.length === 1) {
      setEquipmentLocation(locations[0]);
      const equipment = getEquipment(locations[0].location_id);
      if (equipment.length === 1) {
        setEquipmentEquipment(equipment[0]);
        const interfaces = getInterfaces(equipment[0].id);
        if (interfaces.length === 1) {
          setEquipmentInterface(interfaces[0]);
        }
      }
    }
  }, []);

  const getLocations = (): EquipmentLocation[] => {
    const nResult: EquipmentLocation[] = [];

    props.equipment.filter(n => {
      let exclude = false;
      nResult.forEach(item => {
        if (item.location_id === n.location_id)
          exclude = true;
      });

      if (exclude === false)
        nResult.push({
          location_id: n.location_id,
          location_name: n.location_name
        });
    });

    return nResult;
  }

  const getEquipment = (location_id: number): IEquipmentSummary[] => {
    return props.equipment.filter(n => {
      return n.location_id === location_id
    });
  }

  const getInterfaces = (equipment_id: number): IEquipmentInterfaceSummary[] => {
    const equipment: IEquipmentSummary = props.equipment.filter(n => n.id === equipment_id)[0];
    if (equipment)
      return equipment.interfaces?.filter(n => n.triggers?.indexOf('interfacetrigger.remotescan') !== -1) || [];
    return [];
  }

  const changeLocation = (location: EquipmentLocation | null) => {
    if (location !== null) {
      setEquipmentLocation(location);
      const equipment = getEquipment(location.location_id);
      if (equipment.length === 1) {
        setEquipmentEquipment(equipment[0]);
        const interfaces = getInterfaces(equipment[0].id);
        if (interfaces.length === 1) {
          setEquipmentInterface(interfaces[0]);
        } else {
          setEquipmentInterface(null);
        }
      } else {
        setEquipmentEquipment(null);
        setEquipmentInterface(null);
      }
    } else {
      setEquipmentLocation(null);
      setEquipmentEquipment(null);
      setEquipmentInterface(null);
    }
  };

  const changeEquipment = (equipment: IEquipmentSummary | null) => {
    if (equipment !== null) {
      setEquipmentEquipment(equipment);
      const interfaces = getInterfaces(equipment.id);
      if (interfaces.length === 1) {
        setEquipmentInterface(interfaces[0]);
      } else {
        setEquipmentInterface(null);
      }
    } else {
      setEquipmentEquipment(null);
      setEquipmentInterface(null);
    }
  };

  const changeInterface = (equipmentInterface: IEquipmentInterfaceSummary | null) => {
    setEquipmentInterface(equipmentInterface);
  };

  const lookup = {
    locations: getLocations().sort((a, b) => {
      if (a.location_name > b.location_name) return 1;
      if (a.location_name < b.location_name) return -1;
      return 0;
    }),

    equipment: equipmentLocation !== null ? getEquipment(equipmentLocation?.location_id).sort((a, b) => {
      if (a.name > b.name) return 1;
      if (a.name < b.name) return -1;
      return 0;
    }) : [],

    interfaces: equipmentEquipment !== null ? getInterfaces(equipmentEquipment.id).sort((a, b) => {
      if (a.provider > b.provider) return 1;
      if (a.provider < b.provider) return -1;
      return 0;
    }) : [],
  }

  const LocationComponent = () => {
    return (
      equipmentLocation !== null ?
        <>
          <Grid item key="Location">
            <Card>
              <CardContent>
                <h3>1) Location</h3>
                <Typography gutterBottom style={{ paddingLeft: 20 }}  component="h2" title={`Location Id: ${equipmentLocation.location_id}`}>
                  {equipmentLocation.location_name}
                </Typography>
              </CardContent>

              {lookup.locations.length > 1 ?
                <CardActions>
                  <Button size="small" onClick={n => changeLocation(null)}>Change Location</Button>
                </CardActions>
                :
                <CardActions title={'Only the current location is available.'}>
                  <Button size="small" disabled={true}>Change Location</Button>
                </CardActions>
              }
            </Card>
          </Grid>
          {EquipmentComponent()}
        </>
        :
        <Grid item key="Location">
          <Card>
            <CardContent>
              <h3>1) Pick a Location</h3>
              {lookup.locations.length > 0 ?
                <List component="nav">
                  {lookup.locations.map((location, index) =>
                    <ListItem key={index} title={`Location Id: ${location.location_id}`} button onClick={n => changeLocation(location)}>
                      <ListItemText primary={location.location_name} />
                    </ListItem>
                  )}
                </List>
                :
                <span><span style={{ fontSize: 24 }} title="thinking face">🤔</span> ¯\_(ツ)_/¯<br />No Locations Avaliable.</span>
              }
            </CardContent>
          </Card>
        </Grid>
    )
  }

  const EquipmentComponent = () => {
    return (
      equipmentEquipment !== null ?
        <>
          <Grid item key="Equipment">
            <Card>
              <CardContent>
                <h3>2) Equipment</h3>
                <Typography gutterBottom style={{ paddingLeft: 20 }}  component="h2" title={`Equipment Id: ${equipmentEquipment.id}`}>
                  {equipmentEquipment.name}
                </Typography>
                <Typography gutterBottom title={`Notes`}>
                  {equipmentEquipment.notes}
                </Typography>
              </CardContent>

              {lookup.equipment.length > 1 ?
                <CardActions>
                  <Button size="small" onClick={n => changeEquipment(null)}>Change Equipment</Button>
                </CardActions>
                :
                <CardActions title={'Only the current equipment is available.'}>
                  <Button size="small" disabled={true}>Change Equipment</Button>
                </CardActions>
              }
            </Card>
          </Grid>
          {InterfaceComponent()}
        </>
        :
        <>
          <Grid item key="Equipment">
            <Card>
              <CardContent>
                <h3>2) Pick Equipment</h3>
                {lookup.equipment.length > 0 ?
                  <List component="nav">
                    {lookup.equipment.map((equipment, index) =>
                      <ListItem key={index} title={`Equipment Id: ${equipment.id}`} button onClick={n => changeEquipment(equipment)}>
                        <ListItemText primary={equipment.name} />
                      </ListItem>
                    )}
                  </List>
                  :
                  <span>Something isn't configured correctly.  No Equipment found.</span>
                }
              </CardContent>
            </Card>
          </Grid>
        </>
    )
  }

  const InterfaceComponent = () => {
    return (
      equipmentInterface !== null ?
        <>
          <Grid item key="Interface">
            <Card>
              <CardContent>
                <h3>3) Interface</h3>
                <Typography gutterBottom style={{ paddingLeft: 20 }} component="h2" title={`Interface QRCode: ${equipmentInterface.qr_code}`}>
                  {equipmentInterface.provider}<br />
                  Today's Usage: {equipmentInterface.todays_usage}
                </Typography>
              </CardContent>

              {lookup.interfaces.length > 1 ?
                <CardActions>
                  <Button size="small" onClick={n => changeInterface(null)}>Change Interface</Button>
                </CardActions>
                :
                <CardActions title={'Only the current interface is available.'}>
                  <Button size="small" disabled={true}>Change Interface</Button>
                </CardActions>
              }

            </Card>
          </Grid>
          <RemoteStartComponent membershipId={props.membershipId} userId={props.userId} qrCode={equipmentInterface.qr_code} noGeo={true} />
        </>
        :
        <>
          <Grid item key="Interface">
            <Card>
              <CardContent>
                <h3>3) Pick Interface</h3>
                {lookup.interfaces.length > 0 ?
                  <List component="nav">
                    {lookup.interfaces.map((item, index) =>
                      <ListItem key={index} title={`Interface QRCode: ${item.qr_code}`} button onClick={n => changeInterface(item)}>
                        <ListItemText primary={item.provider} />
                      </ListItem>
                    )}
                  </List>
                  :
                  <span>Something isn't configured correctly.  No Interfaces found.</span>
                }
              </CardContent>
            </Card>
          </Grid>
        </>
    )
  }

  return (
    <Dialog
      aria-labelledby="max-width-dialog-title"

      onClose={props.onClose}
      open={props.show}>
      <DialogTitle>Remote Start Wizard</DialogTitle>
      <DialogContent>
        <Grid container direction="column" justify="flex-start" alignItems="stretch" spacing={2}>
          {LocationComponent()}
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

interface RemoteStartProps {
  qrCode: string
  userId: number
  membershipId: string
  noGeo: boolean
}

export const RemoteStartComponent: React.FC<RemoteStartProps> = (props: RemoteStartProps) => {

  const [phrase, setPhrase] = useState<string>('');
  const [apiTestMode, setApiTestMode] = useState<boolean>(true);
  const [result, setResult] = useState<string | IQRScanResult | undefined | null>('');

  const startRemoteEquipment = (e: any) => {
    e.preventDefault();
    
    if (phrase.trim().toLowerCase() !== 'start')
      return false;
    
    setPhrase('');
    setResult(undefined);

    if (TestMode() === true) {
      setTimeout(() => {
        setResult({
          one_time_use_code: null,
          remote_start: {
            instructions: `FYI, the equipment didn't actually start because we're in Test Mode.`
          }
          
        });
      }, 2000);
    
  } else { // going live.
      const errors: string[] = [];
      if (!props.membershipId || props.membershipId == "") errors.push("Membership Id");
      if (isNaN(props.userId) || props.userId < 1) errors.push("User Id");
      if (props.qrCode.length === 0) errors.push("QRCode");
      
      if (errors.length > 0) {
          setResult({
            one_time_use_code: null,
            remote_start: {
              instructions: `${errors.join(', ')} not specified.`
            }
          });
      } else {
        let config: IQRScanMemberRequest = {
          membership_id: props.membershipId.replace("membership:", ""),
          equipment_id: props.qrCode
        };

        startEquipment(config)
          .then(response => {
            setResult(response)
          });
      }
    }

  }

  useEffect(() => {
    ServerTestMode().then(response => {
      setApiTestMode(response);
    })
  }, [])

  const TestMode = (): boolean => {
    const api = apiTestMode;
    const client = process.env.REACT_APP_TEST_MODE !== "live";
    return api || client;
  }

  return (
    <Grid item>
      <Card>
        <CardContent>
          <h3>4) Start{TestMode() === true && <strong title="Equipment will not actually start, this is only a simulation."> - [TEST MODE]</strong>}</h3>
          <form noValidate autoComplete="off" onSubmit={startRemoteEquipment}>
            {result !== undefined ?
              <TextField id="standard-basic" label="Type 'START' to activate" value={phrase} onChange={n => setPhrase(n.target.value)} />
              :
              <p>Attempting to start the equipment<br/>Please Wait...</p>
            }
          </form>
        </CardContent>
        <CardActions style={{alignItems:'first baseline'}}>
          <Button variant="contained" disabled={phrase.toLowerCase().trim() != 'start'} color="primary" size="small" onClick={startRemoteEquipment}>START</Button>
          
          {result === undefined && // In flight
            <LinearProgress style={{ width: '100%', borderRadius: 50 }} />
          }
          
          {result === null && // Unexpected error
            <Typography gutterBottom component="span" title="An error occured while trying to start the equipment." style={{ cursor: 'default' }}>
              <span style={{ fontSize: 24 }} title="face with symbols over mouth">🤬</span>
              <code title="Guy Flipping Table" style={{ fontSize: 16 }}> ┻┻ ︵ヽ(`Д´)ﾉ︵ ┻┻</code>
              <div style={{ fontSize: 14, maxWidth: 275 }}>
                An unknown error occured while attempting to call the Remote Start API.
              </div>
            </Typography>
          }

          {typeof (result) === 'object' && result && result.remote_start && result.remote_start.instructions && // Success
            <Typography gutterBottom component="span" title="An error occured while trying to start the equipment." style={{ cursor: 'default' }}>
              <span style={{ fontSize: 24 }} title="grinning face">😀</span>
              <code style={{ fontSize: 16 }}> Success</code>
              <div style={{ fontSize: 14, maxWidth: 275 }}>
                {result?.remote_start?.instructions?.length > 0 ? result?.remote_start?.instructions : 'Success'}
              </div>
            </Typography>
          }

          {typeof (result) === 'object'  && result && // Expected Error reported via the API
            <Typography gutterBottom component="span" title="An error occured while trying to start the equipment." style={{ cursor: 'default' }}>
              <span style={{ fontSize: 24 }} title="poop">💩</span>
              <code title="Guy Flipping Tables" style={{ fontSize: 16 }}> (╯°□°）╯︵ ┻┻</code>
              <div style={{ fontSize: 14, maxWidth: 275 }}>{result.remote_start?.instructions}</div>
            </Typography>
          }

        </CardActions>
      </Card>
    </Grid>
  )
}